import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { Users, Plus, Trash2, X, Mail, Building2 } from "lucide-react";

export default function Usuarios() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [operadores, setOperadores] = useState([]);
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "operador", operador_id: "" });

  const loadOperadores = useCallback(async () => {
    const res = await api.get("/operadores");
    setOperadores(res.data);
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/", { replace: true }); return; }
    loadOperadores();
    loadUsers();
  }, [user, navigate, loadOperadores, loadUsers]);

  const create = async () => {
    if (!form.email.trim() || !form.password) {
      toast.error("Completa correo y contraseña");
      return;
    }
    if (form.role === "operador" && !form.operador_id) {
      toast.error("Selecciona un operador para el acceso de operador");
      return;
    }
    try {
      const payload = { ...form };
      if (form.role === "consulta") payload.operador_id = null;
      await api.post("/users", payload);
      toast.success(form.role === "consulta" ? "Acceso de consulta creado" : "Acceso de operador creado");
      setShow(false);
      setForm({ email: "", password: "", name: "", role: "operador", operador_id: "" });
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar el acceso de ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Acceso eliminado");
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <AppShell operadores={operadores} reloadOperadores={loadOperadores}>
      <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-card sticky top-0 z-10">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Work Sans" }}>Accesos de usuario</h1>
          <p className="text-xs text-muted-foreground">Crea accesos de operador (edita su servicio) o de consulta ATTG/externo (ve y descarga todo)</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-user-btn" className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground h-9 px-4 text-sm font-medium shadow-sm hover:bg-primary/90">
          <Plus size={16} /> Nuevo acceso
        </button>
      </header>

      <div className="flex-1 overflow-auto attg-scroll p-6">
        <div className="border border-border rounded-md shadow-sm bg-card overflow-hidden max-w-4xl" data-testid="users-table">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-border">
                <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500">Correo</th>
                <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500">Nombre</th>
                <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500">Rol</th>
                <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500">Operador</th>
                <th className="px-4 py-2.5 text-right font-semibold text-xs uppercase tracking-wider text-slate-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-14 text-center text-muted-foreground">
                  <Users className="mx-auto mb-2 text-slate-300" size={30} />
                  Aún no hay accesos creados.
                </td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border hover:bg-slate-50" data-testid="user-row">
                  <td className="px-4 py-2.5 text-slate-700">{u.email}</td>
                  <td className="px-4 py-2.5 text-slate-700">{u.name}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${u.role === "consulta" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                      {u.role === "consulta" ? "Consulta (ATTG / externo)" : "Operador"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {u.role === "consulta" ? (
                      <span className="text-xs text-slate-400">Todos los operadores</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-2 py-0.5 text-xs">
                        <Building2 size={12} /> {u.operador_nombre || "—"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => remove(u)} data-testid="delete-user" className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-red-50 text-slate-500 hover:text-destructive">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-user-dialog">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShow(false)} />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-background shadow-xl border">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Nuevo acceso</h3>
              <button onClick={() => setShow(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tipo de acceso *</label>
                <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="user-role" className="mt-1 h-9 w-full rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary">
                  <option value="operador">Operador (edita solo su operador)</option>
                  <option value="consulta">Consulta — ATTG / externo (ve y descarga todo, sin editar)</option>
                </select>
              </div>
              {form.role === "operador" && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Operador *</label>
                  <select value={form.operador_id} onChange={(e) => setForm((f) => ({ ...f, operador_id: e.target.value }))} data-testid="user-operador" className="mt-1 h-9 w-full rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary">
                    <option value="">Selecciona un operador…</option>
                    {operadores.map((op) => <option key={op.id} value={op.id}>{op.nombre}</option>)}
                  </select>
                </div>
              )}
              {form.role === "consulta" && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Este acceso permite ver y descargar los datos de <b>todos los operadores</b> (solo lectura). Ideal para personal de la ATTG o entidades externas.
                </p>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Correo *</label>
                <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email" className="mt-1 h-9 w-full rounded-md border border-input px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contraseña *</label>
                <input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password" className="mt-1 h-9 w-full rounded-md border border-input px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre (opcional)</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name" className="mt-1 h-9 w-full rounded-md border border-input px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t">
              <button onClick={() => setShow(false)} className="inline-flex items-center rounded-md border border-border bg-card h-9 px-4 text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={create} data-testid="user-save" className="inline-flex items-center rounded-md bg-primary text-primary-foreground h-9 px-4 text-sm font-medium shadow-sm hover:bg-primary/90">Crear acceso</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
